import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlayIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL + '/scheduler';

export default function SchedulerPage() {
  const queryClient = useQueryClient();
  const [showTimeModal, setShowTimeModal] = useState(false);
  const [selectedJob, setSelectedJob] = useState(null);
  const [newTime, setNewTime] = useState({ hour: '', minute: '' });

  // Fetch jobs
  const { data: jobs, isLoading } = useQuery(['scheduler-jobs'], async () => {
    const response = await axios.get(`${API_BASE_URL}/jobs`);
    return response.data;
  });

  // Run job mutation
  const runJobMutation = useMutation(
    async (jobId) => {
      const response = await axios.post(`${API_BASE_URL}/jobs/${jobId}/run`);
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scheduler-jobs']);
      }
    }
  );

  // Update schedule mutation
  const updateScheduleMutation = useMutation(
    async ({ jobId, hour, minute }) => {
      const response = await axios.put(`${API_BASE_URL}/jobs/${jobId}/schedule`, {
        hour: parseInt(hour),
        minute: parseInt(minute)
      });
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['scheduler-jobs']);
        setShowTimeModal(false);
      }
    }
  );

  const handleRunJob = (jobId) => {
    runJobMutation.mutate(jobId);
  };

  const handleUpdateSchedule = () => {
    if (!selectedJob || !newTime.hour || !newTime.minute) return;
    
    updateScheduleMutation.mutate({
      jobId: selectedJob,
      hour: newTime.hour,
      minute: newTime.minute
    });
  };

  const openTimeModal = (job) => {
    setSelectedJob(job.id);
    // Parse current schedule
    const match = job.trigger.match(/at (\d+):(\d+)/);
    if (match) {
      setNewTime({
        hour: match[1],
        minute: match[2]
      });
    } else {
      setNewTime({ hour: '', minute: '' });
    }
    setShowTimeModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="sm:flex sm:items-center sm:justify-between">
        <h1 className="text-2xl font-semibold text-gray-900">Scheduler Management</h1>
      </div>

      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col">
            <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Job Name
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Next Run Time
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Schedule
                      </th>
                      <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {jobs?.map((job) => (
                      <tr key={job.id}>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                          {job.name}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {new Date(job.next_run_time).toLocaleString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {job.trigger}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleRunJob(job.id)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 mr-2"
                          >
                            <PlayIcon className="h-4 w-4 mr-1" />
                            Run Now
                          </button>
                          <button
                            onClick={() => openTimeModal(job)}
                            className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
                          >
                            <ArrowPathIcon className="h-4 w-4 mr-1" />
                            Update Schedule
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Time Update Modal */}
      {showTimeModal && (
        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900">
                      Update Schedule
                    </h3>
                    <div className="mt-4 grid grid-cols-2 gap-4">
                      <div>
                        <label htmlFor="hour" className="block text-sm font-medium text-gray-700">
                          Hour (0-23)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="23"
                          id="hour"
                          value={newTime.hour}
                          onChange={(e) => setNewTime({ ...newTime, hour: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                      <div>
                        <label htmlFor="minute" className="block text-sm font-medium text-gray-700">
                          Minute (0-59)
                        </label>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          id="minute"
                          value={newTime.minute}
                          onChange={(e) => setNewTime({ ...newTime, minute: e.target.value })}
                          className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm py-2 px-3 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={handleUpdateSchedule}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Update
                </button>
                <button
                  type="button"
                  onClick={() => setShowTimeModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 